import './SplitButton.css';
import { useEffect, useRef, useState } from "react";

export default function PatientSplitButton({
  format = "SMS",
  patients = [],
  placeholders = [],
  defaultFrequency = "week",
  handleCallSelectedPatients,
  handleSMSSelectedPatients,
  getDisabledTooltipMessage,
  isAllSelectedPatientsComplete
}) {
  const [open, setOpen] = useState(false);
  const [frequency, setFrequency] = useState(defaultFrequency); // week | month | 3months | 6months | year
  const menuRef = useRef(null);

  const disabled = !isAllSelectedPatientsComplete();

  // Build option labels using the provided `format`
  const optLabel = (suffix) => `${format} Selected Patients ${suffix}`;
  const OPTIONS = [
    { key: "week",     label: optLabel("every week"),     desc: "Schedule weekly." },
    { key: "month",    label: optLabel("every month"),    desc: "Schedule monthly." },
    { key: "3months",  label: optLabel("every 3 months"), desc: "Schedule quarterly." },
    { key: "6months",  label: optLabel("every 6 months"), desc: "Schedule semiannually." },
    { key: "year",     label: optLabel("every year"),     desc: "Schedule annually." },
  ];

  const selected = OPTIONS.find(o => o.key === frequency) ?? OPTIONS[0];

  // Close on outside click / Escape
  useEffect(() => {
    const onDocClick = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const choose = (key) => {
    setFrequency(key);
    setOpen(false); // collapse after selection
  };

  // Main action routes to the correct handler based on `format`
  const runSelectedAction = () => {
    const payload = { frequency }; // attach frequency for your scheduling logic
    if (format === "Call") {
      // Adjust signature if your handler differs
      return handleCallSelectedPatients(patients, placeholders, payload);
    }
    // format === "SMS"
    return handleSMSSelectedPatients(patients, placeholders, payload);
  };

  return (
    <div className="call-button-wrapper" ref={menuRef}>
      <div className="split-button">
        {/* Main action button: label equals selected option */}
        <button
          className="call-patients-button main"
          onClick={runSelectedAction}
          disabled={disabled}
          aria-label={selected.label}
        >
          <b>{selected.label}</b>
        </button>

        {/* Caret toggle */}
        <button
          className="call-patients-button dropdown-toggle"
          onClick={() => setOpen(v => !v)}
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Choose frequency"
        >
          ▾
        </button>
    </div>

    {/* Disabled tooltip (unchanged) */}
    {!isAllSelectedPatientsComplete() && (
      <div className="button-tooltip">{getDisabledTooltipMessage()}</div>
    )}
    {/* Dropdown menu */}
    {open && (
      <div className="dropdown-menu" role="menu">
        {OPTIONS.map((opt) => (
          <button
            key={opt.key}
            role="menuitemradio"
            aria-checked={opt.key === frequency}
            className={`dropdown-item ${opt.key === frequency ? "selected" : ""}`}
            onClick={() => choose(opt.key)}
          >
            <span className="check">{opt.key === frequency ? "✓" : ""}</span>
            <div className="item-text">
              <div className="item-title">{opt.label}</div>
            </div>
          </button>
        ))}
      </div>
    )}

    </div>
  );
}